import React from "react";
import { Draggable } from "@/components/dnd/Draggable";
import CardArt from "@/components/Card/CardArt";
import { useClientMediaQuery } from "@/lib/hooks/isMobile";
import MobileCardButtons from "@/components/DeckBuilder/Mobile/CardButtons";
import DesktopCardButtons from "@/components/DeckBuilder/Desktop/CardButtons";
import { GameHandler } from "@/interfaces/GameHandler";
import { useAddCard } from "@/stores/deckStore";
import "@/styles/deck-builder.css";

export const DECK_BUILDER_CARD_WIDTH = 90;
export const DECK_BUILDER_CARD_HEIGHT = 125;

function DraggableCard({
  aaCardId,
  addCard,
  setHoveredAACardId,
  cardCount,
  handler,
}: {
  aaCardId: string;
  addCard: any;
  setHoveredAACardId: any;
  cardCount: number;
  handler: GameHandler;
}) {
  const isMobile = useClientMediaQuery("(max-width: 600px)");

  return (
    <div data-id={aaCardId}>
      <Draggable id={aaCardId}>
        <div
          onClick={() => {
            addCard(aaCardId, 1);
          }}
          onMouseEnter={() => {
            setHoveredAACardId(aaCardId);
          }}
          onMouseMove={() => {
            setHoveredAACardId(aaCardId);
          }}
          onMouseLeave={() => {
            setHoveredAACardId(null);
          }}
          className="relative overflow-visible"
        >
          <CardImage
            aaCardId={aaCardId}
            cardCount={cardCount}
            handler={handler}
          />
          {cardCount > 0 && (
            <div
              className="absolute z-20 flex flex-col"
              style={{
                top: "-10px",
                right: "-10px",
              }}
            >
              <div
                className="text-sm font-bold text-black bg-white rounded-full flex justify-center items-center outline outline-1 outline-offset-[-1px] outline-gray-400 shadow-[0_0_6px_rgba(0,0,0,0.3)]"
                style={{ height: "20px", width: "20px" }}
              >
                {cardCount}
              </div>
            </div>
          )}
        </div>
      </Draggable>
    </div>
  );
}

export function CardImage({
  aaCardId,
  opacity,
  cardCount,
  handler,
}: {
  aaCardId: string;
  opacity?: number;
  cardCount?: number;
  handler: GameHandler;
}) {
  const isInDeck = !!(cardCount && cardCount > 0);
  const outlineClass = isInDeck
    ? "outline-white shadow-[0_0_6px_rgba(255,255,255,0.3)] group-hover:shadow-[0_0_10px_rgba(255,255,255,0.4)]"
    : "outline-slate-700 group-hover:outline-white";

  if (aaCardId) {
    const aa = handler.getDeckBuilderCardFunctions().getAAFromCardId(aaCardId);
    const card = handler.getModalCardData(aaCardId);
    const orientation = card
      ? (handler.getCardOrientation?.(card) ?? "portrait")
      : "portrait";

    return (
      <div className="group relative">
        <CardArt
          src={handler.getCardImage(aa.id, "158x220", aa.src)}
          alt={aa.name}
          orientation={orientation}
          width={DECK_BUILDER_CARD_WIDTH}
          height={DECK_BUILDER_CARD_HEIGHT}
          radiusClassName="rounded-[4px]"
          background="bg-neutral-800"
          className="shrink-0"
          imgClassName={`outline outline-2 outline-offset-[-1px] ${outlineClass} ${
            isInDeck ? "brightness-110 animate-in-deck" : ""
          } transition-[outline-color,box-shadow] duration-150`}
          imgStyle={opacity !== undefined ? { opacity } : undefined}
        />
        {isInDeck && (
          <div className="pointer-events-none absolute inset-0 z-10 rounded-[4px] bg-black/40" />
        )}
      </div>
    );
  } else {
    return (
      <div className="group relative">
        <div
          className={`rounded-[4px] bg-neutral-800 outline outline-2 outline-offset-[-1px] ${outlineClass}`}
          style={{
            width: DECK_BUILDER_CARD_WIDTH,
            height: DECK_BUILDER_CARD_HEIGHT,
          }}
        />
      </div>
    );
  }
}

const DisplayCard = React.memo(
  function DisplayCard({
    aaCardId,
    cardData,
    handleOpen,
    setHoveredAACardId,
    cardCount,
    handler,
  }: {
    aaCardId: string;
    cardData: any;
    handleOpen: any;
    setHoveredAACardId: any;
    cardCount: number;
    handler: GameHandler;
  }) {
    const addCard = useAddCard();
    const isMobile = useClientMediaQuery("(max-width: 600px)");
    const cardHeight = "120px";

    let buttons;
    if (isMobile) {
      buttons = null;
    } else {
      buttons = (
        <div
          className={
            "absolute top-0 -left-2 z-40 flex flex-col group-hover:visible"
          }
        >
          <DesktopCardButtons handleOpen={handleOpen} aaCardId={aaCardId} />
        </div>
      );
    }

    return (
      <div className="relative p-1 flex justify-center items-center">
        <div className="group relative h-[131px]">
          {buttons}
          <DraggableCard
            aaCardId={aaCardId}
            addCard={addCard}
            setHoveredAACardId={setHoveredAACardId}
            cardCount={cardCount}
            handler={handler}
          />
        </div>
      </div>
    );
  },
  (prevProps, nextProps) => {
    return prevProps.cardCount === nextProps.cardCount;
  },
);

export default DisplayCard;
